import { useState, useEffect, useRef } from "react";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminChat, sendAdminChatMessage } from "@/lib/firestoreService";

export const AdminChat = () => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const { user, profile } = useAuth();
  const messages = useAdminChat();
  const bottomRef = useRef(null);
  const seenCountRef = useRef(0);

  // Track unread when panel is closed
  useEffect(() => {
    if (open) {
      seenCountRef.current = messages.length;
      setUnread(0);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    } else {
      const newCount = messages.length - seenCountRef.current;
      if (newCount > 0) setUnread(newCount);
    }
  }, [messages, open]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await sendAdminChatMessage({
        text: text.trim(),
        from: user.id,
        sender: profile?.name || user.email?.split("@")[0],
      });
      setText("");
    } catch {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Fixed chat toggle button — bottom right */}
      <button
        onClick={() => { setOpen((v) => !v); setUnread(0); }}
        title="Admin Chat"
        className="fixed bottom-5 right-5 z-50 w-11 h-11 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] rounded-full shadow-lg flex items-center justify-center transition-colors"
      >
        <MessageSquare className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-[#7A3B25] border-2 border-[#F5E6CC] text-[#F5E6CC] text-[9px] font-bold rounded-full flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {/* Floating chat panel — opens above the button */}
      {open && (
        <div className="fixed bottom-20 right-5 z-50 w-80 flex flex-col bg-[#F5E6CC] border border-[#5A2D1D]/25 rounded-sm shadow-2xl" style={{ height: 420 }}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2.5 bg-[#5A2D1D] rounded-t-sm flex-shrink-0">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-3.5 h-3.5 text-[#F5E6CC]" />
              <span className="text-sm font-bold text-[#F5E6CC]">Admin Chat</span>
              <span className="text-[10px] text-[#F5E6CC]/50 font-normal">· admins only</span>
            </div>
            <button onClick={() => setOpen(false)} className="text-[#F5E6CC]/60 hover:text-[#F5E6CC] transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 min-h-0">
            {messages.length === 0 ? (
              <div className="text-center text-xs text-[#2E1A12]/40 py-8">
                No messages yet. Start the conversation!
              </div>
            ) : (
              messages.map((m) => {
                const isMe = m.from === user.id;
                const initials = (m.sender || "A")[0].toUpperCase();
                const time = m.at?.seconds
                  ? new Date(m.at.seconds * 1000).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
                  : "";
                return (
                  <div key={m.id} className={`flex gap-2 ${isMe ? "flex-row-reverse" : ""}`}>
                    <div className={`w-6 h-6 rounded-full flex-shrink-0 grid place-items-center text-[10px] font-bold ${
                      isMe ? "bg-[#5A2D1D] text-[#F5E6CC]" : "bg-[#556B2F] text-[#F5E6CC]"
                    }`}>
                      {initials}
                    </div>
                    <div className={`max-w-[72%] flex flex-col gap-0.5 ${isMe ? "items-end" : "items-start"}`}>
                      {!isMe && (
                        <span className="text-[10px] font-bold text-[#556B2F] uppercase tracking-wide leading-none">
                          {m.sender}
                        </span>
                      )}
                      <div className={`text-sm px-3 py-2 leading-relaxed break-words ${
                        isMe
                          ? "bg-[#5A2D1D] text-[#F5E6CC] rounded-sm rounded-tr-none"
                          : "bg-white/70 text-[#2E1A12] border border-[#5A2D1D]/10 rounded-sm rounded-tl-none"
                      }`}>
                        {m.text}
                      </div>
                      {time && (
                        <span className="text-[10px] text-[#2E1A12]/35">{time}</span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="flex gap-2 p-3 border-t border-[#5A2D1D]/15 flex-shrink-0">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Message admins… (Enter to send)"
              className="flex-1 px-3 py-2 rounded-sm bg-white/60 border border-[#5A2D1D]/20 focus:border-[#5A2D1D] focus:outline-none text-sm text-[#2E1A12] placeholder:text-[#2E1A12]/40"
            />
            <button
              onClick={send}
              disabled={sending || !text.trim()}
              className="px-3 py-2 bg-[#5A2D1D] hover:bg-[#7A3B25] disabled:bg-[#5A2D1D]/30 text-[#F5E6CC] rounded-sm transition-colors flex-shrink-0"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
    </>
  );
};
